import axios, { isAxiosError } from "axios";

import { queryClient } from "#app/http/ReactQueryProvider";
import { AccessTokenSingleton } from "#modules/auth/domain/AccessTokenSingleton";
import { isUserDisabledError } from "#modules/auth/domain/isUserDisabledError";
import { handleDisabledUser } from "#modules/auth/infra/handleDisabledUser";
import { confirmOptimizationInvalidation } from "#modules/optimizations/infra/optimizationInvalidationConfirmation";
import { camelize } from "#shared/helpers/ApiHelper";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { OPTIMIZATION_INVALIDATION_CONFIRMATION_HEADER } from "shared";

export const fetcher = axios.create({
  baseURL: import.meta.env.VITE_BASE_API_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

fetcher.interceptors.request.use((config) => {
  const accessToken = AccessTokenSingleton.getInstance().getAccessToken();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

fetcher.interceptors.response.use(
  (config) => {
    // Skip camelization for binary responses (blob, arraybuffer)
    if (config.config.responseType === "blob" || config.config.responseType === "arraybuffer") {
      return config;
    }

    config.data = camelize(config.data);

    return config;
  },
  async (error: unknown) => {
    if (isAxiosError(error) && error.response) {
      if (isUserDisabledError(error.response.status, error.response.data)) {
        void handleDisabledUser();
      }

      const isConfirmed = await confirmOptimizationInvalidation(
        error.response.status,
        error.response.data,
      );
      if (isConfirmed && error.config) {
        const confirmedResponse = await fetcher.request({
          ...error.config,
          headers: {
            ...error.config.headers,
            [OPTIMIZATION_INVALIDATION_CONFIRMATION_HEADER]: "true",
          },
        });
        void queryClient.invalidateQueries({ queryKey: queryKeys.optimizations.all });
        return confirmedResponse;
      }
    }

    return Promise.reject(error);
  },
);
